import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import SidebarLinkGroup from "./SidebarLinkGroup";
import {
  LuLandPlot,
  LuLayoutDashboard,
  LuSquareActivity,
} from "react-icons/lu";
import { MdOutlineMergeType, MdPerson } from "react-icons/md";
import { GiArchitectMask } from "react-icons/gi";
import { FaBridgeCircleCheck } from "react-icons/fa6";
import {
  RiAlignItemHorizontalCenterFill,
  RiQuoteText,
  RiUserFill,
} from "react-icons/ri";
import { TbAirConditioningDisabled } from "react-icons/tb";
import { PiPersonFill } from "react-icons/pi";
import { AiOutlineTransaction } from "react-icons/ai";
import { FaQuestionCircle, FaSitemap, FaWindowRestore } from "react-icons/fa";
import { GrManual } from "react-icons/gr";
import { SiMaterialformkdocs } from "react-icons/si";
import { IoIosArrowDown, IoMdSettings } from "react-icons/io";
import logo from "../../images/logo/we.png";

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  sidebarCollapsed,
  setSidebarCollapsed,
}) => {
  const { pathname } = useLocation();
  const sidebarRef = useRef(null);

  const storedSidebarExpanded = localStorage.getItem("sidebar-expanded");
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded !== null ? storedSidebarExpanded === "true" : false
  );
  const [allowedMenuIDs, setAllowedMenuIDs] = useState([]);
  const [openGroupIndex, setOpenGroupIndex] = useState(null);

  useEffect(() => {
    const userId = sessionStorage.getItem("userId");
    const permissionData = sessionStorage.getItem("userMenuPermissions");

    if (userId === "1" || permissionData === "ALL") {
      setAllowedMenuIDs("ALL");
    } else {
      const parsed = JSON.parse(permissionData || "[]");
      const ids = parsed
        .filter((p) => p.isView || p.isInsert || p.isUpdate || p.isDelete)
        .map((p) => p.menuId.toString());
      setAllowedMenuIDs(ids);
    }
  }, []);

  const MENU_GROUPS = [
    {
      title: "Dashboard",
      titleIcon: <AiOutlineTransaction className="w-[20px] h-[20px]" />,
      basePath: "/dashboard",
      items: [
        {
          label: "Dashboard",
          path: "/dashboard",
          icon: <LuLandPlot />,
          menuId: "15",
        },
      ],
    },
    {
      title: "Masters",
      titleIcon: <LuLayoutDashboard className="w-[20px] h-[20px]" />,
      basePath: "/master",
      items: [
        {
          label: "Owner",
          path: "/master/owner",
          icon: <MdPerson />,
          menuId: "1",
        },
        {
          label: "Architect",
          path: "/master/architect",
          icon: <GiArchitectMask />,
          menuId: "2",
        },
        {
          label: "Fabricator",
          path: "/master/fabricator",
          icon: <FaBridgeCircleCheck />,
          menuId: "3",
        },
        {
          label: "Item",
          path: "/master/item",
          icon: <RiAlignItemHorizontalCenterFill />,
          menuId: "4",
        },
        {
          label: "Terms",
          path: "/master/terms",
          icon: <TbAirConditioningDisabled />,
          menuId: "5",
        },
        {
          label: "Customer",
          path: "/master/customer",
          icon: <PiPersonFill />,
          menuId: "6",
        },
        {
          label: "Coating-Type",
          path: "/master/coatingType",
          icon: <MdOutlineMergeType />,
          menuId: "16",
        },
        {
          label: "Fins-Item",
          path: "/master/fins-item",
          icon: <FaSitemap />,
          menuId: "17",
        },
      ],
    },
    {
      title: "Transaction",
      titleIcon: <AiOutlineTransaction className="w-[20px] h-[20px]" />,
      basePath: "/transaction",
      items: [
        {
          label: "Site-Details",
          path: "/transaction/sites",
          icon: <LuLandPlot />,
          menuId: "7",
        },
      ],
    },
    {
      title: "Quotes",
      titleIcon: <FaQuestionCircle className="w-[20px] h-[20px]" />,
      basePath: "/quotes",
      items: [
        {
          label: "Window-Quotation",
          path: "/quotes/window-quotation",
          icon: <FaWindowRestore />,
          menuId: "8",
        },
        {
          label: "Manual-Quotation",
          path: "/quotes/manual-quotation",
          icon: <GrManual />,
          menuId: "9",
        },
        {
          label: "Material-Quotation",
          path: "/quotes/material-quotation",
          icon: <SiMaterialformkdocs />,
          menuId: "10",
        },
        {
          label: "Fins-Quotation",
          path: "/quotes/fins-quotation",
          icon: <RiQuoteText />,
          menuId: "18",
        },
      ],
    },
    {
      title: "Settings",
      titleIcon: <IoMdSettings className="w-[20px] h-[20px]" />,
      basePath: "/settings",
      items: [
        {
          label: "Users",
          path: "/settings/user-master",
          icon: <RiUserFill />,
          menuId: "11",
        },
        {
          label: "Menu-Permission",
          path: "/settings/menu-permission",
          icon: <LuSquareActivity />,
          menuId: "12",
        },
        {
          label: "Activity-Log",
          path: "/settings/activitylog",
          icon: <LuSquareActivity />,
          menuId: "13",
        },
      ],
    },
    {
      title: "Reports",
      titleIcon: <AiOutlineTransaction className="w-[20px] h-[20px]" />,
      basePath: "/reports",
      items: [
        {
          label: "Reports",
          path: "/reports",
          icon: <LuLandPlot />,
          menuId: "14",
        },
      ],
    },
  ];

  const filteredMenuGroups = MENU_GROUPS.map((group) => {
    const filteredItems =
      allowedMenuIDs === "ALL"
        ? group.items
        : group.items.filter((item) => allowedMenuIDs.includes(item.menuId));
    return filteredItems.length ? { ...group, items: filteredItems } : null;
  }).filter(Boolean);

  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
      setOpenGroupIndex(null);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [sidebarOpen, setSidebarOpen]);

  useEffect(() => {
    localStorage.setItem("sidebar-expanded", sidebarExpanded.toString());
    if (sidebarExpanded) document.body.classList.add("sidebar-expanded");
    else document.body.classList.remove("sidebar-expanded");
  }, [sidebarExpanded]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setSidebarOpen(false);
        setOpenGroupIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setSidebarOpen]);

  return (
    <aside
      ref={sidebarRef}
      className={`absolute left-0 top-0 z-9999 flex h-screen ${
        sidebarCollapsed ? "w-22 overflow-y-visible" : "w-50 overflow-y-auto"
      } flex-col bg-primary duration-300 ease-linear lg:static lg:translate-x-0 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div className="flex items-center justify-between gap-2 px-6 py-5.5 relative">
        <NavLink to="/" className="flex items-center gap-2">
          {sidebarCollapsed ? (
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-md">
              <img src={logo} alt="Logo" className="w-18 h-18 object-contain" />
            </div>
          ) : (
            <h1 className="font-bold text-xl text-white">H-LINE CRM</h1>
          )}
        </NavLink>
      </div>

      <div
        className={`flex-1 flex flex-col ${
          sidebarCollapsed ? "items-center" : ""
        } duration-300 ease-linear`}
      >
        <nav className="py-4 px-4">
          <ul
            className={`mb-6 flex flex-col gap-1.5 ${
              sidebarCollapsed ? "items-center" : ""
            }`}
          >
            {filteredMenuGroups.map((group, index) => {
              const isGroupActive = group.items.some((item) =>
                pathname.startsWith(item.path)
              );

              const expandedOpen = openGroupIndex === index;

              return (
                <div key={index} className="relative group">
                  <SidebarLinkGroup activeCondition={isGroupActive}>
                    {(handleClick, open) => (
                      <>
                        <NavLink
                          to={group.basePath}
                          className={`group relative flex items-center gap-2.5 rounded-sm py-2 font-medium duration-300 ease-in-out bg-primary ${
                            isGroupActive ? "text-white" : "text-white/50 "
                          } ${
                            sidebarCollapsed ? "justify-center px-8" : "px-4"
                          } group-hover:text-white`}
                          onClick={(e) => {
                            e.preventDefault();
                            if (sidebarCollapsed) {
                              setSidebarExpanded(true);
                            } else {
                              setOpenGroupIndex((prev) =>
                                prev === index ? null : index
                              );
                            }
                          }}
                        >
                          <span>{group.titleIcon}</span>
                          {!sidebarCollapsed && <span>{group.title}</span>}
                          {!sidebarCollapsed && (
                            <IoIosArrowDown
                              className={`absolute top-1/2 right-4 -translate-y-1/2 fill-current transition-transform ${
                                expandedOpen ? "rotate-180" : "rotate-0"
                              }`}
                            />
                          )}
                        </NavLink>

                        {sidebarCollapsed && (
                          <div
                            className={`
                              absolute left-22 top-0 z-50 w-60 bg-primary p-3 rounded-md shadow-lg
                              hidden group-hover:block
                            `}
                          >
                            <div className="flex items-center">
                              <span className="text-white text-start font-bold">
                                {group.title}
                              </span>
                            </div>
                            <ul className="mt-4 mb-5.5 flex flex-col gap-2.5">
                              {group.items.map((item, idx) => (
                                <li key={idx}>
                                  <NavLink
                                    to={item.path}
                                    className={({ isActive }) =>
                                      `group relative flex items-center gap-2.5 rounded-md px-4 py-1.5 font-medium duration-300 ease-in-out text-sm ${
                                        isActive
                                          ? "text-white"
                                          : "text-white/60"
                                      } hover:bg-white/10`
                                    }
                                  >
                                    {item.icon}
                                    {item.label}
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {!sidebarCollapsed && (
                          <div
                            className={`transition-all duration-300 ${
                              expandedOpen
                                ? "max-h-screen"
                                : "max-h-0 overflow-hidden"
                            }`}
                          >
                            <ul className="mt-2 mb-3 flex flex-col gap-1.5 pl-4">
                              {group.items.map((item, idx) => (
                                <li key={idx}>
                                  <NavLink
                                    to={item.path}
                                    className={({ isActive }) =>
                                      `flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm font-medium duration-200 ${
                                        isActive
                                          ? "text-white"
                                          : "text-white/60"
                                      } hover:bg-white/10`
                                    }
                                  >
                                    {item.icon}
                                    {item.label}
                                  </NavLink>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </SidebarLinkGroup>
                </div>
              );
            })}
          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
